import { ClipboardEvent, KeyboardEvent, useRef, useState } from "react";
import { Paperclip, Send, Square } from "lucide-react";
import { Button } from "~/components/ui/button";
import { cn } from "~/lib/utils";
import { PartialSkill } from "~/services/skills";
import { ConnectorsPopover, FAKE_CONNECTORS } from "./ConnectorsPopover";
import { SkillsPopover } from "./SkillsPopover";

interface ComposerProps {
  // Inert composer — used by the loading skeleton on conversation mount.
  disabled?: boolean;
  // True while a run is streaming. Swaps the Send button for a Stop button
  // that calls `onCancel` instead of submitting.
  isRunning?: boolean;
  // True between the moment the user clicks Stop and the moment the agent
  // emits RUN_FINISHED. Disables the button to prevent duplicate cancels.
  isCancelling?: boolean;
  onCancel?: () => void;
  onSubmit: (text: string) => void;
  placeholder?: string;
  // Skills available in this conversation's context. Empty list during
  // loading or when no skills apply is rendered as a friendly empty state.
  skills?: PartialSkill[];
}

export const Composer = ({
  disabled,
  isRunning,
  isCancelling,
  onCancel,
  onSubmit,
  placeholder = "What would you like to know?",
  skills = [],
}: ComposerProps) => {
  const inputRef = useRef<HTMLDivElement>(null);
  const [hasText, setHasText] = useState(false);
  const [enabledConnectors, setEnabledConnectors] = useState<string[]>(
    FAKE_CONNECTORS.map((c) => c.id)
  );

  const toggleConnector = (id: string) =>
    setEnabledConnectors((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const insertSkill = (skillName: string) => {
    const el = inputRef.current;
    if (!el) return;
    const current = el.innerText;
    const needsSpace = current.length > 0 && !current.endsWith(" ") && !current.endsWith("\n");
    el.innerText = `${current}${needsSpace ? " " : ""}${skillName} `;
    setHasText(!!el.innerText.trim());
    el.focus();
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  };

  const submit = () => {
    const text = inputRef.current?.innerText.trim() ?? "";
    if (!text || disabled || isRunning) return;
    if (inputRef.current) inputRef.current.innerText = "";
    setHasText(false);
    onSubmit(text);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  // Strip styling on paste so a contenteditable behaves like a <textarea>:
  // pull plain text out of the clipboard and insert it at the caret. Using
  // `execCommand("insertText")` preserves the browser's undo stack, which a
  // manual Range.insertNode would clobber. We also normalize CRLF and trim
  // bracketing newlines — many apps tack on a trailing \n when copying that
  // turns into a stray empty line; internal paragraph breaks are preserved.
  const handlePaste = (e: ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const text = e.clipboardData
      .getData("text/plain")
      .replace(/\r\n?/g, "\n")
      .replace(/^\n+|\n+$/g, "");
    if (!text) return;
    document.execCommand("insertText", false, text);
  };

  const canSubmit = hasText && !disabled && !isRunning;
  const canCancel = Boolean(isRunning && onCancel && !isCancelling);
  // While running, the action button stops the run; otherwise it sends.
  const buttonActive = isRunning ? canCancel : canSubmit;
  const buttonLabel = isRunning ? (isCancelling ? "Cancelling…" : "Stop generation") : "Send";

  return (
    <div
      className="mx-auto w-full max-w-[60rem] cursor-text rounded-2xl border border-border bg-background"
      onMouseDown={(e) => {
        // Focus the editor when the user clicks anywhere in the shell that
        // isn't a button or the editor itself; preserves native button clicks.
        if ((e.target as HTMLElement).closest("button, [contenteditable]")) return;
        e.preventDefault();
        inputRef.current?.focus();
      }}
    >
      <div
        ref={inputRef}
        role="textbox"
        aria-label="Message"
        aria-multiline="true"
        contentEditable
        suppressContentEditableWarning
        data-placeholder={placeholder}
        onInput={() => setHasText(!!inputRef.current?.innerText.trim())}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        className={cn(
          "max-h-64 min-h-12 overflow-y-auto whitespace-pre-wrap break-words px-4 pt-3 text-sm outline-none",
          "empty:before:pointer-events-none empty:before:text-muted-foreground/50",
          "empty:before:content-[attr(data-placeholder)]"
        )}
      />
      <div className="flex items-center justify-between gap-2 px-2 py-2">
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-8 text-muted-foreground"
            aria-label="Attach file"
            disabled
          >
            <Paperclip className="size-4" />
          </Button>
          <ConnectorsPopover
            connectors={FAKE_CONNECTORS}
            enabledIds={enabledConnectors}
            onToggle={toggleConnector}
          />
          <SkillsPopover skills={skills} onSelect={insertSkill} />
        </div>
        <Button
          type="button"
          onClick={isRunning ? onCancel : submit}
          disabled={!buttonActive}
          size="icon"
          variant="ghost"
          aria-label={buttonLabel}
          className={cn(
            "group size-8 rounded-full",
            buttonActive
              ? "bg-foreground text-background hover:bg-foreground hover:text-background"
              : "bg-muted text-muted-foreground disabled:pointer-events-auto disabled:cursor-default"
          )}
        >
          {isRunning ? (
            <Square
              className={cn(
                "size-3 fill-current",
                buttonActive && "group-hover:animate-icon-pulse"
              )}
            />
          ) : (
            <Send className={cn("size-3.5", buttonActive && "group-hover:animate-icon-pulse")} />
          )}
        </Button>
      </div>
    </div>
  );
};
